import type {components} from '../../shared/api/v1/openapi.types';

type AuthResponseDto = components['schemas']['AuthResponseDto'];

export type AuthUpdatedDetail = {
  access_token?: string;
  refresh_token?: string;
  auth?: AuthResponseDto;
  cleared?: boolean;
};

export const authUpdatedEventName = 'auth:updated';

const tokenKey = 'token';
const refreshTokenKey = 'refresh_token';
const authKey = 'auth';

const canUseStorage = () => typeof window !== 'undefined';

const readJson = <T>(value?: string | null) => {
  if (!value) {
    return undefined;
  }
  try {
    return JSON.parse(value) as T;
  } catch {
    return undefined;
  }
};

const dispatchAuthUpdated = (detail: AuthUpdatedDetail) => {
  if (!canUseStorage()) {
    return;
  }
  window.dispatchEvent(new CustomEvent<AuthUpdatedDetail>(authUpdatedEventName, { detail }));
};

export const getStoredAuth = (): AuthResponseDto | undefined => {
  if (!canUseStorage()) {
    return undefined;
  }
  return readJson<AuthResponseDto>(localStorage.getItem(authKey));
};

export const getStoredAccessToken = (): string | undefined => {
  if (!canUseStorage()) {
    return undefined;
  }
  return localStorage.getItem(tokenKey) ?? getStoredAuth()?.access_token;
};

export const getStoredRefreshToken = (): string | undefined => {
  if (!canUseStorage()) {
    return undefined;
  }
  return localStorage.getItem(refreshTokenKey) ?? getStoredAuth()?.refresh_token;
};

export const updateStoredAuth = (auth: AuthResponseDto) => {
  if (!canUseStorage()) {
    return;
  }
  localStorage.setItem(authKey, JSON.stringify(auth));
  localStorage.setItem(tokenKey, auth.access_token);
  localStorage.setItem(refreshTokenKey, auth.refresh_token);
  dispatchAuthUpdated({
    auth,
    access_token: auth.access_token,
    refresh_token: auth.refresh_token,
  });
};

export const updateStoredTokens = (accessToken: string, refreshToken: string) => {
  if (!canUseStorage()) {
    return;
  }
  const storedAuth = getStoredAuth();
  const nextAuth = storedAuth
    ? {
      ...storedAuth,
      access_token: accessToken,
      refresh_token: refreshToken,
    }
    : undefined;
  if (nextAuth) {
    localStorage.setItem(authKey, JSON.stringify(nextAuth));
  }
  localStorage.setItem(tokenKey, accessToken);
  localStorage.setItem(refreshTokenKey, refreshToken);
  dispatchAuthUpdated({
    auth: nextAuth,
    access_token: accessToken,
    refresh_token: refreshToken,
  });
};

export const clearStoredAuth = () => {
  if (!canUseStorage()) {
    return;
  }
  localStorage.removeItem(tokenKey);
  localStorage.removeItem(refreshTokenKey);
  localStorage.removeItem(authKey);
  dispatchAuthUpdated({ cleared: true });
};

export const isAccessTokenExpired = (value?: string) => {
  if (!value) {
    return false;
  }
  try {
    const [, payloadBase64] = value.split('.');
    if (!payloadBase64) {
      return false;
    }
    const normalized = payloadBase64.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const decoded = atob(padded);
    const payload = JSON.parse(decoded) as { exp?: number };
    if (!payload.exp) {
      return false;
    }
    const nowSeconds = Math.floor(Date.now() / 1000);
    return payload.exp <= nowSeconds;
  } catch {
    return false;
  }
};
